import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import dashboardService from '../services/dashboardService';
import AdminCommandCenter from './dashboard/AdminCommandCenter';
import ReceptionCommandCenter from './dashboard/ReceptionCommandCenter';
import TechnicianCommandCenter from './dashboard/TechnicianCommandCenter';
import DoctorCommandCenter from './dashboard/DoctorCommandCenter';
import RadiologistCommandCenter from './dashboard/RadiologistCommandCenter';
import ManagerCommandCenter from './dashboard/ManagerCommandCenter';
import './dashboard/Dashboard.css';

export const Dashboard = () => {
  const { user, can } = useAuth();

  // Active Center State (defaults to authenticated center)
  const [selectedCentreId, setSelectedCentreId] = useState(user?.centreId || null);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState('0'); // '0' = Off, '30' = 30s, '60' = 60s
  const [loading, setLoading] = useState(true);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [stats, setStats] = useState({
    phlebotomy: null,
    accession: null,
    pathology: null,
    approval: null,
    settlement: null,
    radiology: null,
    recentRegistrations: null,
    security: null,
    outsource: null,
    health: null
  });

  // Role View Management (Administrator can preview any role view)
  const isSuperAdmin = user?.roleName?.toUpperCase() === 'ADMINISTRATOR' || user?.roleId === 1;
  const [activeRoleView, setActiveRoleView] = useState(() => {
    const role = user?.roleName?.toUpperCase() || '';
    if (role.includes('FRONT') || role.includes('RECEPTION')) return 'RECEPTION';
    if (role.includes('PHLEBOTOM') || role.includes('TECHNICIAN')) return 'TECHNICIAN';
    if (role.includes('PATHOLOG') || role.includes('VALIDAT') || role.includes('DOCTOR')) return 'DOCTOR';
    if (role.includes('RADIO')) return 'RADIOLOGIST';
    if (role.includes('CENTRE ADMIN') || role.includes('MANAGER')) return 'MANAGER';
    return 'ADMIN';
  });

  // Extract authorized centers from availableWorkspaces
  const authorizedCentres = useMemo(() => {
    const workspaces = user?.availableWorkspaces || [];
    const uniqueCentres = [];
    const map = new Set();
    workspaces.forEach((ws) => {
      if (ws.centreId && !map.has(ws.centreId)) {
        map.add(ws.centreId);
        uniqueCentres.push({
          centreId: ws.centreId,
          centreName: ws.centreName,
          centreCode: ws.centreCode
        });
      }
    });
    // Fallback if no workspaces explicitly listed
    if (uniqueCentres.length === 0 && user?.centreId) {
      uniqueCentres.push({
        centreId: user.centreId,
        centreName: user.centreName,
        centreCode: user.centreCode
      });
    }
    return uniqueCentres;
  }, [user]);

  // Active Center Display Name
  const activeCentreObj = authorizedCentres.find((c) => c.centreId === Number(selectedCentreId));
  const activeCentreName = activeCentreObj
    ? `${activeCentreObj.centreName} (${activeCentreObj.centreCode || 'ID:' + activeCentreObj.centreId})`
    : user?.centreName || 'Central Lab';

  // Load Dashboard Data with Center-Isolation
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await dashboardService.loadAllDashboardData(selectedCentreId);
      setStats(data);
      setLastRefreshedAt(new Date());
    } catch (err) {
      console.error('Error fetching dashboard telemetry:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedCentreId]);

  // Initial Load & On Center Change
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Auto-Refresh Effect
  useEffect(() => {
    const seconds = parseInt(autoRefreshInterval, 10);
    if (seconds <= 0) return;

    const intervalId = setInterval(() => {
      fetchDashboardData();
    }, seconds * 1000);

    return () => clearInterval(intervalId);
  }, [autoRefreshInterval, fetchDashboardData]);

  // Role Renderer
  const renderRoleDeck = () => {
    switch (activeRoleView) {
      case 'RECEPTION':
        return <ReceptionCommandCenter stats={stats} loading={loading} activeCentreName={activeCentreName} can={can} />;
      case 'TECHNICIAN':
        return <TechnicianCommandCenter stats={stats} loading={loading} activeCentreName={activeCentreName} can={can} />;
      case 'DOCTOR':
        return <DoctorCommandCenter stats={stats} loading={loading} activeCentreName={activeCentreName} can={can} />;
      case 'RADIOLOGIST':
        return <RadiologistCommandCenter stats={stats} loading={loading} activeCentreName={activeCentreName} can={can} />;
      case 'MANAGER':
        return <ManagerCommandCenter stats={stats} loading={loading} activeCentreName={activeCentreName} can={can} />;
      case 'ADMIN':
      default:
        return <AdminCommandCenter stats={stats} loading={loading} activeCentreName={activeCentreName} can={can} />;
    }
  };

  return (
    <div className="lis-dashboard-wrapper">
      {/* 1. COMPACT DASHBOARD CONTEXT TOOLBAR */}
      <div className="lis-compact-toolbar">
        <div className="lis-toolbar-left">
          <div className="lis-toolbar-title-wrap">
            <svg className="lis-toolbar-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <rect x="3" y="3" width="7" height="9" rx="1" />
              <rect x="14" y="3" width="7" height="5" rx="1" />
              <rect x="14" y="12" width="7" height="9" rx="1" />
              <rect x="3" y="16" width="7" height="5" rx="1" />
            </svg>
            <span className="lis-toolbar-title">Workbench</span>
          </div>

          <div className="lis-toolbar-sep" aria-hidden="true"></div>

          {/* Operating Center Selector Chip */}
          <div className="lis-toolbar-chip lis-chip-center" title="Active Operating Center">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-3" />
            </svg>
            <label htmlFor="centre-select" className="sr-only">Operating Center</label>
            <select
              id="centre-select"
              className="lis-chip-select"
              value={selectedCentreId || ''}
              onChange={(e) => setSelectedCentreId(e.target.value ? Number(e.target.value) : null)}
            >
              {isSuperAdmin && <option value="">All Centers (Consolidated)</option>}
              {authorizedCentres.map((c) => (
                <option key={c.centreId} value={c.centreId}>
                  {c.centreName} {c.centreCode ? `(${c.centreCode})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Designated Role Chip */}
          <div className="lis-toolbar-chip lis-chip-role" title={`Designated User Role: ${user?.roleName || 'Clinical Staff'}`}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <span>{user?.roleName || 'Clinical Staff'}</span>
          </div>

          {/* Live Telemetry Pulse Chip */}
          <div className="lis-toolbar-chip lis-chip-live" title="Real-time telemetry stream active">
            <span className="lis-live-pulse-dot"></span>
            <span>Live</span>
          </div>
        </div>

        <div className="lis-toolbar-right">
          {/* Shift / Period Filter */}
          <div className="lis-toolbar-ctrl" title="Operational Shift / Date Range">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <select id="shift-select" className="lis-chip-select" defaultValue="today">
              <option value="today">Today's Shift</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
            </select>
          </div>

          {/* Auto Refresh Setting */}
          <div className="lis-toolbar-ctrl" title="Auto-refresh interval">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
            <select
              id="refresh-interval"
              className="lis-chip-select"
              value={autoRefreshInterval}
              onChange={(e) => setAutoRefreshInterval(e.target.value)}
            >
              <option value="0">Manual</option>
              <option value="30">Auto: 30s</option>
              <option value="60">Auto: 60s</option>
            </select>
          </div>

          {/* Manual Refresh Button */}
          <button
            type="button"
            className="lis-compact-btn-refresh"
            onClick={fetchDashboardData}
            disabled={loading}
            title="Refresh operational telemetry now"
          >
            <svg
              className={loading ? 'spinning' : ''}
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
            >
              <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
            </svg>
            <span>{loading ? 'Syncing...' : 'Refresh'}</span>
          </button>

          {/* Last Synced Badge */}
          <span className="lis-toolbar-timestamp" title="Last synchronized time">
            {lastRefreshedAt ? lastRefreshedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : 'Syncing'}
          </span>
        </div>
      </div>

      {/* 2. ADMINISTRATOR ROLE SWITCHER STRIP (COMPACT PILLS) */}
      {isSuperAdmin && (
        <div className="lis-role-strip-compact">
          <span className="lis-role-strip-label">COMMAND DECK:</span>
          <div className="lis-role-pills-row">
            <button
              type="button"
              className={`lis-role-pill ${activeRoleView === 'ADMIN' ? 'active' : ''}`}
              onClick={() => setActiveRoleView('ADMIN')}
            >
              Administrator
            </button>
            <button
              type="button"
              className={`lis-role-pill ${activeRoleView === 'RECEPTION' ? 'active' : ''}`}
              onClick={() => setActiveRoleView('RECEPTION')}
            >
              Reception / Front Desk
            </button>
            <button
              type="button"
              className={`lis-role-pill ${activeRoleView === 'TECHNICIAN' ? 'active' : ''}`}
              onClick={() => setActiveRoleView('TECHNICIAN')}
            >
              Lab Tech & Phlebotomist
            </button>
            <button
              type="button"
              className={`lis-role-pill ${activeRoleView === 'DOCTOR' ? 'active' : ''}`}
              onClick={() => setActiveRoleView('DOCTOR')}
            >
              Pathologist / Doctor
            </button>
            <button
              type="button"
              className={`lis-role-pill ${activeRoleView === 'RADIOLOGIST' ? 'active' : ''}`}
              onClick={() => setActiveRoleView('RADIOLOGIST')}
            >
              Radiologist
            </button>
            <button
              type="button"
              className={`lis-role-pill ${activeRoleView === 'MANAGER' ? 'active' : ''}`}
              onClick={() => setActiveRoleView('MANAGER')}
            >
              Center Manager
            </button>
          </div>
        </div>
      )}

      {/* 4. ACTIVE ROLE COMMAND CENTER DECK */}
      {renderRoleDeck()}
    </div>
  );
};

export default Dashboard;
